import { getAuthToken, getDeviceUUID, getPinToken } from 'src/utils/storage';
import axios, { AxiosInstance } from 'axios';
import queryString from 'query-string';

import {
  onAxiosInstanceNoSessionRequestFulfill,
  onAxiosInstanceRequestFulfill,
  onAxiosInstanceRequestReject,
  onAxiosInstanceResponseFulfill,
  onAxiosInstanceResponseReject,
} from './interceptorUtils/callbacks';
import { getDomainConfig } from './domainConfig';
import { userDataStore } from '../store';
import { getEnterPinPageURL } from '../helpers';
import { ROUTES } from '../constants/routes';

/**
 * Get the appropriate base URL based on environment and domain
 * @param hostname - Optional hostname for SSR scenarios
 * @param nodeServerURL - Optional override for node server URL
 */
const getBaseURL = (hostname?: string) => {


  return getDomainConfig(hostname ?? '').apiBaseUrl;
};


const redirectToEnterPin = () => {
  if (window.location.pathname !== ROUTES.ENTER_PIN) {
    window.location.href = getEnterPinPageURL();
  }
};
export const createAxiosInstance = (hostname: string): AxiosInstance => {

  let pinToken = '';

  try {
    pinToken = getPinToken();
  } catch (error) {
    console.error('Error reading pin token while creating axios instance: ', error);
    redirectToEnterPin();

    // Return a stub instance so module-level initialisation doesn't crash
    // while the browser navigates away.
    return axios.create({ baseURL: getBaseURL(hostname) });
  }

  if (!pinToken) {
    redirectToEnterPin();

    // Return a stub instance — the page is navigating away, so this instance
    // will never be used for real requests.
    return axios.create({ baseURL: getBaseURL(hostname) });
  }

  const axiosInstance = axios.create({
    baseURL: getBaseURL(hostname),
    headers: {
      Authorization: getAuthToken(),
      'X-USER-CAMPAIGN': pinToken,
      'x-platform': 'web',
      'X-APP-ID': getDomainConfig(hostname).appId,
      'x-device-type': 'desktop',
      'x-device-id': getDeviceUUID() ?? '',
      'X-CLIENT-TOKEN': 'WEB',
      'X-USER-ID': userDataStore.getState().userData?.user_account_id ?? '',
      'X-PLATFORM-VERSION': '1.0', //TODO
      'X-DEVICE-ID-V2': getDeviceUUID(),
    },
    paramsSerializer: params =>
      queryString.stringify(params, { arrayFormat: 'comma' }),
  });

  axiosInstance.interceptors.request.use(
    onAxiosInstanceRequestFulfill,
    onAxiosInstanceRequestReject
  );

  axiosInstance.interceptors.response.use(
    onAxiosInstanceResponseFulfill,
    onAxiosInstanceResponseReject
  );

  return axiosInstance;
};

export const createAxiosInstanceNoSession = (
  hostname: string
) => {
  const axiosInstanceNoSession = axios.create({
    baseURL: getBaseURL(hostname),
    headers: {
      'x-platform': 'web',
      'X-APP-ID': getDomainConfig(hostname).appId,
      'x-device-type': 'desktop',
    },
    paramsSerializer: params =>
      queryString.stringify(params, { arrayFormat: 'comma' }),
  });

  axiosInstanceNoSession.interceptors.request.use(
    onAxiosInstanceNoSessionRequestFulfill,
    onAxiosInstanceRequestReject
  );

  return axiosInstanceNoSession;
};

export const axiosInstance: AxiosInstance = createAxiosInstance(window.location.hostname);
export const axiosInstanceNoSession: AxiosInstance = createAxiosInstanceNoSession(window.location.hostname);

let _abortController: AbortController | null = null;

// NOTE: Only the most recently created signal can be cancelled via generalizedCancelRequest().
// Calling createCancelSignal() for a new request overwrites the previous controller — prior
// in-flight requests become uncancellable. This matches the old CancelToken behavior.
export function createCancelSignal() {
  _abortController = new AbortController();
  return _abortController.signal;
}

export function generalizedOnCancelHandling(error: unknown) {
  // You can use this for any API, dont create new method everytime.
  // That is why the term generalizedOnCancelHandling
  return axios.isCancel(error);
}

declare global {
  interface Window {
    cancelVar?: () => void;
  }
}

export function generalizedCancelRequest() {
  // You can use this for any API, dont create new method everytime.
  // That is why the term generalizedCancelRequest
  if (_abortController) {
    _abortController.abort();
    _abortController = null;
  }

  if (typeof window !== 'undefined' && window.cancelVar) {
    window.cancelVar();
  }
}
